import { useEffect, useRef } from 'react';

import { Modal } from '@/components/Modal';

import { useClaimSubmission } from '@/hooks/useClaimSubmission';

import type { IClaimSubmission } from '@/types/claim';

import { formatCurrency } from '@/utils/formatting';

interface ClaimConfirmationModalProps {
  claimData: IClaimSubmission;
  categoryName: string;
  receiptFile: File;
  onCancel: () => void;
  onSuccess: () => void;
}

function ClaimConfirmationModal({
  claimData,
  categoryName,
  receiptFile,
  onCancel,
  onSuccess,
}: ClaimConfirmationModalProps) {
  const { mutate: submitClaimMutation, isPending, error } = useClaimSubmission();
  const receiptObjectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (receiptObjectUrlRef.current) {
        URL.revokeObjectURL(receiptObjectUrlRef.current);
        receiptObjectUrlRef.current = null;
      }
    };
  }, [receiptFile]);

  const handleReceiptClick = () => {
    if (!receiptObjectUrlRef.current) {
      receiptObjectUrlRef.current = URL.createObjectURL(receiptFile);
    }
    window.open(receiptObjectUrlRef.current, '_blank', 'noopener,noreferrer');
  };

  const handleConfirm = () => {
    submitClaimMutation(
      { claimData, receiptFile },
      {
        onSuccess: () => {
          onSuccess();
        },
      },
    );
  };

  return (
    <Modal
      title="Confirm Your Claim"
      onClose={onCancel}
      maxWidth="md"
      zIndex={60}
      closeButtonDisabled={isPending}
    >
      <div className="space-y-6" data-testid="claim-confirmation-modal">
        {claimData.details && (
          <dl>
            <dt className="mb-2 text-base text-gray-600">Description</dt>
            <dd className="text-base whitespace-pre-wrap text-gray-900">{claimData.details}</dd>
          </dl>
        )}

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Total Amount</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(claimData.amount, true)}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Category</p>
          <span className="rounded bg-red-100 px-3 py-1 text-sm font-medium text-red-600">
            {categoryName}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-base text-gray-600">Receipt</p>
          <button
            type="button"
            onClick={handleReceiptClick}
            className="flex min-w-0 flex-1 items-center justify-end gap-2 text-red-500 hover:text-red-600"
            aria-label={`View receipt ${receiptFile.name}`}
            title={receiptFile.name}
          >
            <svg className="size-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="truncate text-sm font-medium">{receiptFile.name}</span>
          </button>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error.message || 'Failed to submit claim'}
          </div>
        )}

        <button
          data-testid="confirm-submit-button"
          onClick={handleConfirm}
          disabled={isPending}
          className="w-full rounded-lg bg-red-500 p-4 text-lg font-bold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPending ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </Modal>
  );
}

export { ClaimConfirmationModal };
